import * as THREE from 'three';

/**
 * ドラッグ位置ガイド（自前モジュール・GPL非依存）。
 *
 * 構え中（aimReady）、canvas 上のドラッグで操作可能な領域を screen-space に
 * 半透明オーバーレイで可視化する。
 *
 * **操作実態と表現の対応**:
 *   - 手玉中心 ↔ 前方ガイド端: **aim 専用領域**（kind='aim'・perpendicular swing）。
 *   - 手玉中心 ↔ キュー手元   : **aim ＋ 仰角の両用領域**（kind='cue'・cuedrag→ 横=aim/縦=仰角）。
 *
 * → 「aim 操作が可能な範囲」は **手元→ball→前方端** の連続した 1 本の線分。**両側で同色**（cyan）。
 *    「仰角操作が可能な範囲」は ball→キュー手元 だけ。**aim cyan の上に orange を重ねて**
 *    「ここは aim も仰角も両方できる」を視覚で表す（半透明オーバーレイ）。
 *
 * **camera 後方クランプ**:
 *   標準キュー長 1.5m は狙撃カム ON（camera 半径 ≈1.0m）・低仰角時にキュー手元が
 *   camera 後方へ突き抜け、`Vector3.project()` の透視除算が破綻して帯が極端に縮む。
 *   `effLen = min(cue.length, camDist × 0.85)` で常に frustum 内に収まるよう
 *   クランプし、orange 帯と grabKind 判定の両方を実用領域へ広げる。
 *   ※ controls.ts grabKind 側も同じクランプを行う（drag-guide と grabKind の対応関係を維持）。
 *
 * 設計理由（World-space 描画ではなく SVG/screen-space を採用）:
 *   - AIM_GRAB_PX=44 は CSS px 基準。capsule 幅も CSS px で表現する必要がある。
 *   - 3D シーンに置くと球/クッションに occluded されガイドが消える事故が起きる。
 *   - canvas 上に SVG（pointer-events:none）を重ねる方式は他 HUD と z-index で共存可能。
 */

export interface DragGuide {
  /** 1フレーム更新（カメラ・手玉位置・キュー長・仰角から screen-space 線分を計算して描画）。 */
  update(p: {
    readonly camera: THREE.Camera;
    readonly ballX: number;
    readonly ballY: number;
    readonly ballRadius: number;
    readonly aimAngle: number;
    readonly elevation: number;
    /** GPL Cue の長さ（m）。バット側手元の 3D 位置計算に使う。 */
    readonly cueLength: number;
    /** Aim 前方ガイドの長さ（m, world）。controls.ts grabKind の前方端と完全同期させる。 */
    readonly forwardLen: number;
    /** 表示可否。false で全要素を非表示。 */
    readonly visible: boolean;
    /** アクティブなドラッグジェスチャ。'aim'=cyan のみ表示／'elevate'=yellow のみ表示／
     *  'idle'=両方表示（既定の非ドラッグ状態）。操作中のガイドだけを残してユーザーに
     *  「今どのモードか」を色で伝える。 */
    readonly activeMode: 'idle' | 'aim' | 'elevate';
  }): void;
  /** 非表示にする（visible=false と同義の即時版）。 */
  hide(): void;
  /** SVG 要素を DOM から除去して解放。 */
  dispose(): void;
}

// halo capsule（掴み範囲帯）の不透明度。aim と cue が重なる区間で合計 ≈0.27 になる薄め設定。
const AIM_HALO_OPACITY = 0.12;
const CUE_HALO_OPACITY = 0.18; // 仰角オーバーレイは少し濃いめ＝重なる場所が見分けやすい
const SHAFT_OPACITY = 0.5;
const SHAFT_WIDTH_PX = 1.4;

// swing 矢印（ドラッグで起きる動きの方向を示す双方向矢印）のスタイル。
const SWING_OFFSET_PX = 28; // 手玉中心から矢印先端までの距離
const SWING_LEN_PX = 16; // 矢印頭の長さ
const SWING_HEAD_HALF_PX = 6;
const SWING_OPACITY = 0.8;

const COLOR_AIM = '#5ecbff'; // うすいシアン（aim 操作可能領域・手元↔前方端の全長）
const COLOR_CUE = '#ffd45e'; // うすい黄（仰角操作可能領域・手元↔ball・aim 上にオーバーレイ）

/** ball→handle の連続線分のうち、camera frustum 内に収まる effective なキュー長。 */
const effectiveCueLength = (
  cueLength: number,
  ballX: number,
  ballY: number,
  ballZ: number,
  camera: THREE.Camera,
): number => {
  const cdx = camera.position.x - ballX;
  const cdy = camera.position.y - ballY;
  const cdz = camera.position.z - ballZ;
  const camDist = Math.hypot(cdx, cdy, cdz);
  // 0.85 = camera 近傍にめり込まない安全係数（frustum 内＝NDC z<1 を保証）。
  return Math.min(cueLength, camDist * 0.85);
};

// 3D 点を canvas 矩形内の CSS px 座標へ投影。camera 後方（local z >= 0）は visible=false。
// `Vector3.project()` だけだと NDC z<1 を pass しても透視除算で値が破綻するケースがあるため、
// camera-local z で「カメラより手前か」を厳密判定する。
const tmpLocal = new THREE.Vector3();
const tmpNdc = new THREE.Vector3();
const projectToScreen = (
  v: THREE.Vector3,
  camera: THREE.Camera,
  rect: DOMRect,
): { x: number; y: number; visible: boolean } => {
  tmpLocal.copy(v).applyMatrix4(camera.matrixWorldInverse);
  if (tmpLocal.z >= -1e-4) {
    // カメラと同じ平面 or 後方。透視除算が破綻するため非表示。
    return { x: 0, y: 0, visible: false };
  }
  tmpNdc.copy(v).project(camera);
  return {
    x: rect.left + (tmpNdc.x * 0.5 + 0.5) * rect.width,
    y: rect.top + (-tmpNdc.y * 0.5 + 0.5) * rect.height,
    visible: true,
  };
};

// SVG 名前空間で要素を生成。
const svgEl = <K extends keyof SVGElementTagNameMap>(
  tag: K,
): SVGElementTagNameMap[K] =>
  document.createElementNS('http://www.w3.org/2000/svg', tag);

export const createDragGuide = (
  canvas: HTMLCanvasElement,
  grabRadiusPx: number,
): DragGuide => {
  // SVG ルート: canvas に重ねる（pointer-events:none で操作はすべて canvas へ透過）。
  // mobile-hud-ux-guidelines.md §2.補助オーバーレイ（操作不能・領域マップ対象外）。
  const svg = svgEl('svg');
  svg.setAttribute('aria-hidden', 'true');
  Object.assign(svg.style, {
    position: 'fixed',
    left: '0',
    top: '0',
    width: '100vw',
    height: '100vh',
    pointerEvents: 'none',
    zIndex: '5',
  } as Partial<CSSStyleDeclaration>);
  document.body.appendChild(svg);

  // 描画順（SVG は後 appendChild が前面）:
  //   ① aim halo（cyan・最も太く・最も後ろ）
  //   ② cue halo（yellow・aim halo に重なる cue 区間のみ・aim の上に描画して合成）
  //   ③ aim shaft（cyan・細線・halo 中心軸）
  //   ④ aim swing 矢印（cyan ←→）
  //   ⑤ cue swing 矢印（yellow ↑↓）
  const aimHalo = svgEl('line');
  aimHalo.setAttribute('stroke', COLOR_AIM);
  aimHalo.setAttribute('stroke-width', String(grabRadiusPx * 2));
  aimHalo.setAttribute('stroke-opacity', String(AIM_HALO_OPACITY));
  aimHalo.setAttribute('stroke-linecap', 'round');
  aimHalo.setAttribute('fill', 'none');
  svg.appendChild(aimHalo);

  const cueHalo = svgEl('line');
  cueHalo.setAttribute('stroke', COLOR_CUE);
  cueHalo.setAttribute('stroke-width', String(grabRadiusPx * 2));
  cueHalo.setAttribute('stroke-opacity', String(CUE_HALO_OPACITY));
  cueHalo.setAttribute('stroke-linecap', 'round');
  cueHalo.setAttribute('fill', 'none');
  svg.appendChild(cueHalo);

  const aimShaft = svgEl('line');
  aimShaft.setAttribute('stroke', COLOR_AIM);
  aimShaft.setAttribute('stroke-width', String(SHAFT_WIDTH_PX));
  aimShaft.setAttribute('stroke-opacity', String(SHAFT_OPACITY));
  aimShaft.setAttribute('stroke-linecap', 'round');
  aimShaft.setAttribute('fill', 'none');
  svg.appendChild(aimShaft);

  const aimArrow1 = svgEl('polygon');
  aimArrow1.setAttribute('fill', COLOR_AIM);
  aimArrow1.setAttribute('fill-opacity', String(SWING_OPACITY));
  svg.appendChild(aimArrow1);
  const aimArrow2 = svgEl('polygon');
  aimArrow2.setAttribute('fill', COLOR_AIM);
  aimArrow2.setAttribute('fill-opacity', String(SWING_OPACITY));
  svg.appendChild(aimArrow2);

  const cueArrow1 = svgEl('polygon');
  cueArrow1.setAttribute('fill', COLOR_CUE);
  cueArrow1.setAttribute('fill-opacity', String(SWING_OPACITY));
  svg.appendChild(cueArrow1);
  const cueArrow2 = svgEl('polygon');
  cueArrow2.setAttribute('fill', COLOR_CUE);
  cueArrow2.setAttribute('fill-opacity', String(SWING_OPACITY));
  svg.appendChild(cueArrow2);

  const allElements: SVGElement[] = [
    aimHalo,
    cueHalo,
    aimShaft,
    aimArrow1,
    aimArrow2,
    cueArrow1,
    cueArrow2,
  ];

  const setAllVisible = (visible: boolean): void => {
    for (const el of allElements) {
      el.style.display = visible ? '' : 'none';
    }
  };

  const setSegment = (
    line: SVGLineElement,
    ax: number,
    ay: number,
    bx: number,
    by: number,
  ): void => {
    line.setAttribute('x1', ax.toFixed(1));
    line.setAttribute('y1', ay.toFixed(1));
    line.setAttribute('x2', bx.toFixed(1));
    line.setAttribute('y2', by.toFixed(1));
  };

  // 手玉中心 (cx,cy) から (nx,ny) 単位ベクトルの ±方向に「ドラッグ方向の矢印」を 2 つ描画。
  // 先端は手玉から OFFSET、底辺は先端からさらに LEN（=ドラッグ方向に矢印頭が向く）。
  const setSwingArrows = (
    poly1: SVGPolygonElement,
    poly2: SVGPolygonElement,
    cx: number,
    cy: number,
    nx: number,
    ny: number,
  ): void => {
    const tx = -ny; // 矢印頭の底辺方向（軸 n に直交）
    const ty = nx;
    const writeArrow = (poly: SVGPolygonElement, sign: number): void => {
      const tipX = cx + nx * sign * SWING_OFFSET_PX;
      const tipY = cy + ny * sign * SWING_OFFSET_PX;
      const baseX = tipX + nx * sign * SWING_LEN_PX;
      const baseY = tipY + ny * sign * SWING_LEN_PX;
      const p1x = baseX + tx * SWING_HEAD_HALF_PX;
      const p1y = baseY + ty * SWING_HEAD_HALF_PX;
      const p2x = baseX - tx * SWING_HEAD_HALF_PX;
      const p2y = baseY - ty * SWING_HEAD_HALF_PX;
      poly.setAttribute(
        'points',
        `${tipX.toFixed(1)},${tipY.toFixed(1)} ${p1x.toFixed(1)},${p1y.toFixed(1)} ${p2x.toFixed(1)},${p2y.toFixed(1)}`,
      );
    };
    writeArrow(poly1, +1);
    writeArrow(poly2, -1);
  };

  // 再利用バッファ（毎フレーム new しない＝webgl-3d スキル §リソース管理）。
  const tmpBall = new THREE.Vector3();
  const tmpHandle = new THREE.Vector3();
  const tmpForward = new THREE.Vector3();

  return {
    update(p) {
      if (!p.visible) {
        setAllVisible(false);
        return;
      }
      const rect = canvas.getBoundingClientRect();
      // ① 手玉中心。grabKind の `c = worldToScreen(cb.x, cb.y, ballRadius)` と一致。
      tmpBall.set(p.ballX, p.ballY, p.ballRadius);
      const c = projectToScreen(tmpBall, p.camera, rect);
      // ② 前方ガイド端: cb + (cos a, sin a) * forwardLen。
      const dx = Math.cos(p.aimAngle);
      const dy = Math.sin(p.aimAngle);
      tmpForward.set(
        p.ballX + dx * p.forwardLen,
        p.ballY + dy * p.forwardLen,
        p.ballRadius,
      );
      const f = projectToScreen(tmpForward, p.camera, rect);
      // ③ キュー手元（仰角で持ち上がる）: camera 距離でクランプして frustum 内に収める。
      const effLen = effectiveCueLength(
        p.cueLength,
        p.ballX,
        p.ballY,
        p.ballRadius,
        p.camera,
      );
      const cosE = Math.cos(p.elevation);
      const sinE = Math.sin(p.elevation);
      tmpHandle.set(
        p.ballX - dx * effLen * cosE,
        p.ballY - dy * effLen * cosE,
        p.ballRadius + effLen * sinE,
      );
      const h = projectToScreen(tmpHandle, p.camera, rect);

      // 球が画面外なら全て隠す（手玉中心 c.visible が偽＝aim も cue も意味を成さない）。
      if (!c.visible) {
        setAllVisible(false);
        return;
      }
      // 操作中のガイドのみ表示（activeMode='aim' は cyan のみ／'elevate' は yellow のみ／'idle' は両方）。
      const showAim = p.activeMode !== 'elevate';
      const showCue = p.activeMode !== 'aim';
      aimHalo.style.display = showAim ? '' : 'none';
      aimShaft.style.display = showAim ? '' : 'none';
      aimArrow1.style.display = showAim ? '' : 'none';
      aimArrow2.style.display = showAim ? '' : 'none';
      cueHalo.style.display = showCue ? '' : 'none';
      cueArrow1.style.display = showCue ? '' : 'none';
      cueArrow2.style.display = showCue ? '' : 'none';

      // Aim halo: handle ↔ forward end の連続線分（aim 操作の全範囲＝両側同色）。
      // h or f のどちらかが投影破綻ならその側を c で代用（線分が縮退する＝最低限 ball 中心は描く）。
      const aimAx = h.visible ? h.x : c.x;
      const aimAy = h.visible ? h.y : c.y;
      const aimBx = f.visible ? f.x : c.x;
      const aimBy = f.visible ? f.y : c.y;
      setSegment(aimHalo, aimAx, aimAy, aimBx, aimBy);
      setSegment(aimShaft, aimAx, aimAy, aimBx, aimBy);

      // Aim swing 矢印 ←→（aim 軸に直交＝screen 上で「左右に振る」操作）。
      const sdx = aimBx - aimAx;
      const sdy = aimBy - aimAy;
      const slen = Math.hypot(sdx, sdy);
      if (slen > 1e-3) {
        const nx = -sdy / slen;
        const ny = sdx / slen;
        setSwingArrows(aimArrow1, aimArrow2, c.x, c.y, nx, ny);
      }

      // Cue halo: ball ↔ effective handle（仰角 + aim の両用領域・aim halo にオーバーレイ）。
      // handle が投影破綻時は cue halo を縮退（c-c の 0 長線分＝実質非表示）。
      const cueBx = h.visible ? h.x : c.x;
      const cueBy = h.visible ? h.y : c.y;
      setSegment(cueHalo, c.x, c.y, cueBx, cueBy);

      // Cue swing 矢印 ↑↓（screen 垂直＝controls.ts cuedrag 縦移動=仰角操作と一致）。
      // SVG y は下が正なので、画面「上向き」は ny=-1。
      setSwingArrows(cueArrow1, cueArrow2, c.x, c.y, 0, -1);
    },
    hide() {
      setAllVisible(false);
    },
    dispose() {
      svg.remove();
    },
  };
};

/**
 * 公開ヘルパ: controls.ts grabKind の effective キュー長計算と完全に同期する。
 * drag-guide.ts と controls.ts で同じロジックを使うことで「見える範囲」と「掴める範囲」が一致する。
 */
export const effectiveCueLengthForGrab = effectiveCueLength;
